import { countBy } from "lodash-es";

import ChatMessageContentType from "@mapmycustomers/shared/enum/chat/ChatMessageContentType";
import ChatProposalApplyOutcome from "@mapmycustomers/shared/enum/chat/ChatProposalApplyOutcome";
import { ChatProposal } from "@mapmycustomers/shared/types/chat/ChatContent";
import { FieldAgentApplyResultItem } from "@mapmycustomers/shared/types/chat/FieldAgentChat";

// Shared by the card's own Accept/Discard clicks and by the sagas that settle them, so the rep's
// decision and its outcome describe the same card in the same words.
const proposalAnalyticsProps = (proposals: ChatProposal[]) => ({
  proposalActionTypes: proposals.map((proposal) => proposal.action.type),
  proposalCount: proposals.length,
});

// The layout the rep is actually looking at. A de-brief always draws the sectioned list, even with
// a single row, so the visible count alone would mislabel it as standalone.
export const resolveCardVariant = (
  contentType: ChatMessageContentType,
  visible: ChatProposal[]
): "bundled" | "standalone" =>
  contentType !== ChatMessageContentType.DEBRIEF && visible.length === 1 ? "standalone" : "bundled";

// `outcomes` is keyed by whatever the platform returned, which a warehouse cannot give a column
// per key, so the one number an analyst actually asks for rides alongside it as a plain scalar:
// how many rows ended up the way the rep asked.
//
// A set rather than one value because a discard has two landing outcomes. `expired` counts: the row
// lapsed and will never execute, which is what discarding it was for (mmc-server
// apply-service.ts:417-421). `stale` deliberately does not — it only means the row was already
// settled by someone else (`:414-415`), and that settlement may have been an APPLY, in which case
// the rep's discard achieved the opposite of what they asked. `outcomes` still carries it.
export const applyOutcomeProps = <T extends object>(
  trackingProps: T,
  results: FieldAgentApplyResultItem[],
  landedOutcomes: ChatProposalApplyOutcome[]
) => ({
  ...trackingProps,
  landedCount: results.filter((result) => landedOutcomes.includes(result.outcome)).length,
  // A count per outcome, never the rows themselves — the rows carry record data.
  outcomes: countBy(results, (result) => result.outcome),
});

export default proposalAnalyticsProps;
